/* ---------------------------------------------------------------------------
   SAUDI NATIONAL DAY OFFER  —  free gift engine

   Strategy: the gift is added to the cart BEFORE the theme fires its own
   /cart/add.js request. That way the cart drawer HTML the theme receives back
   already contains the gift, so there is no race between our request and the
   drawer re-render.

   A background reconcile still runs on every cart change to remove the gift
   when the cart no longer qualifies, and to force its quantity back to 1.
--------------------------------------------------------------------------- */

(() => {
  const cfg = window.NationalDayOffer;
  if (!cfg || !cfg.enabled || !cfg.giftVariantId) return;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const GIFT_ID = String(cfg.giftVariantId);
  const GIFT_FLAG = '_free_gift';
  const EXCLUDED = new Set((cfg.excludedProductIds || []).map(String));
  const MIN_QTY = cfg.tier1Qty || 1;

  let busy = false;
  let lastCart = null;
  // While an add-to-cart is in flight the cart may briefly hold only the gift.
  // Don't remove it during that window.
  let graceUntil = 0;

  /* ------------------------------------------------------------------ utils */

  const isGift = (item) =>
    String(item.variant_id) === GIFT_ID ||
    !!(item.properties && item.properties[GIFT_FLAG]);

  const isExcluded = (item) => EXCLUDED.has(String(item.product_id));

  function eligibleQty(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      if (isGift(item) || isExcluded(item)) return total;
      return total + item.quantity;
    }, 0);
  }

  const giftLine = (cart) => ((cart && cart.items) || []).find(isGift);

  const isCartPage = () =>
    (window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.pageType === 'cart') ||
    document.querySelector('.cart-page') !== null;

  // Guard so a failing gift removal can never put the cart page in a reload loop
  const RELOAD_KEY = 'nd_offer_reloaded';
  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1';
    sessionStorage.removeItem(RELOAD_KEY);
  } catch (e) {
    alreadyReloaded = true;
  }

  const getCart = () =>
    fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const post = (url, body) =>
    fetch(ROOT + url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(async (r) => {
      const payload = await r.json();
      if (!r.ok) {
        throw new Error(payload.description || payload.message || 'Cart request failed');
      }
      return payload;
    });

  const addGift = () =>
    post('cart/add.js', {
      items: [{ id: Number(GIFT_ID), quantity: 1, properties: { [GIFT_FLAG]: 'true' } }]
    });

  /* -------------------------------------------- add the gift before the add */

  // The bundle block registers this to tell us which additional variants the
  // shopper picked (e.g. the second shade in a "buy 2" bundle).
  function extraItems() {
    try {
      const fn = window.NationalDayOffer.getExtraItems;
      if (typeof fn !== 'function') return [];
      return (fn() || []).filter((item) => item && item.id);
    } catch (e) {
      console.warn('[National Day Offer] could not read the extra bundle items', e);
      return [];
    }
  }

  async function ensureGiftBeforeAdd() {
    graceUntil = Date.now() + 10000;

    // Each item goes in its own request so one sold-out variant can't take the
    // whole bundle down with it.
    const items = extraItems();

    try {
      const cart = await getCart();
      if (!giftLine(cart)) {
        items.push({ id: Number(GIFT_ID), quantity: 1, properties: { [GIFT_FLAG]: 'true' } });
      }
    } catch (e) {
      console.warn('[National Day Offer] could not read the cart', e);
    }

    for (const item of items) {
      try {
        await post('cart/add.js', { items: [item] });
      } catch (e) {
        console.warn('[National Day Offer] could not add bundle item', item, e);
      }
    }
  }

  window.NationalDayOffer.prepareBuyNow = ensureGiftBeforeAdd;

  function isAddToCartButton(button) {
    if (!button) return false;
    // Shopify's dynamic checkout button goes straight to checkout - leave it alone
    if (button.closest('.shopify-payment-button')) return false;

    const formId = button.getAttribute('form');
    const form = formId ? document.getElementById(formId) : button.form;
    if (!form) return false;

    const action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1;
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[type="submit"], input[type="submit"]');
      if (!button || !isAddToCartButton(button)) return;

      // Second pass: let the theme handle it normally
      if (button.dataset.ndPassthrough === '1') {
        delete button.dataset.ndPassthrough;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      ensureGiftBeforeAdd().then(() => {
        button.dataset.ndPassthrough = '1';
        button.click();
      });
    },
    true
  );

  /* --------------------------------------------------- background reconcile */

  async function reconcile(cart) {
    if (busy) return;
    busy = true;

    try {
      cart = cart && cart.items ? cart : await getCart();
      const qty = eligibleQty(cart);
      const line = giftLine(cart);
      let changed = false;

      // Once the cart genuinely qualifies, the add-to-cart grace window has
      // served its purpose. Clearing it here means a later removal is never
      // blocked by an add that happened seconds earlier.
      if (qty >= MIN_QTY) graceUntil = 0;

      if (qty >= MIN_QTY && !line) {
        await addGift();
        changed = true;
      } else if (qty < MIN_QTY && line && Date.now() > graceUntil) {
        await post('cart/change.js', { id: line.key, quantity: 0 });
        changed = true;
      } else if (line && line.quantity !== 1) {
        await post('cart/change.js', { id: line.key, quantity: 1 });
        changed = true;
      }

      if (changed) {
        lastCart = await getCart();

        if (isCartPage()) {
          // The cart page removes line items with a full page navigation, so
          // there is no live section to refresh - reload once instead.
          if (!alreadyReloaded) {
            sessionStorage.setItem(RELOAD_KEY, '1');
            window.location.reload();
            return;
          }
        } else {
          // Give the theme's own drawer render a moment to finish before we ask
          // it to refresh, otherwise it can overwrite us with stale markup.
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }, 200);
        }
      } else {
        lastCart = cart;
      }
    } catch (e) {
      console.warn('[National Day Offer] reconcile failed', e);
    } finally {
      busy = false;
    }
  }

  /* -------------------------------------------------------------- listeners */

  document.addEventListener('cart:change', (event) => {
    reconcile(event.detail && event.detail.cart);
  });

  window.addEventListener('pageshow', () => reconcile());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcile());
  } else {
    reconcile();
  }

  window.NationalDayOffer.eligibleQty = eligibleQty;
  window.NationalDayOffer.refresh = reconcile;
})();
